// Entities: Player, Enemy, projectiles, pickups.
import Phaser from 'phaser';
import { CONFIG, META_UPGRADES } from './data.js';

// --------------------------------------------------------------
// PLAYER
// --------------------------------------------------------------
export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    super(scene, x, y, 'player');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCollideWorldBounds(true);
    this.setDepth(20);
    this.body.setSize(18, 18).setOffset(5, 5);

    this.maxHp = CONFIG.playerStartHp;
    this.hp = this.maxHp;
    this.moveSpeed = CONFIG.playerStartSpeed;
    this.damageMult = 1.0;
    this.fireRateMult = 1.0;
    this.projectileSpeedMult = 1.0;
    this.pickupRadius = CONFIG.pickupBase;
    this.xpMult = 1.0;
    this.creditMult = 1.0;
    this.critChance = 0.05;
    this.critMult = 2.0;
    this.regenPerSec = 0;
    this.regenAccum = 0;
    this.armor = 0;
    this.rerolls = 0;
    this.reviveTokens = 0;
    this.startingWeapons = ['blaster'];

    this.weaponLevels = {};
    this.passiveLevels = {};
    this.weaponCooldowns = {};

    this.level = 1;
    this.xp = 0;
    this.xpNeeded = CONFIG.xpToLevel(1);
    this.invuln = 0;

    this._flashTimer = 0;
  }

  applyMetaUpgrades(saveMeta) {
    for (const key in saveMeta) {
      const lvl = saveMeta[key];
      const def = META_UPGRADES[key];
      if (def && lvl > 0) def.apply(this, lvl);
    }
  }

  grantStartingWeapons() {
    for (const k of this.startingWeapons) {
      if (!this.weaponLevels[k]) {
        this.weaponLevels[k] = 1;
        this.weaponCooldowns[k] = 0;
      }
    }
  }

  addXp(amount) {
    this.xp += amount * this.xpMult;
    let leveled = false;
    while (this.xp >= this.xpNeeded) {
      this.xp -= this.xpNeeded;
      this.level += 1;
      this.xpNeeded = CONFIG.xpToLevel(this.level);
      leveled = true;
    }
    return leveled;
  }

  takeDamage(amount) {
    if (this.invuln > 0) return false;
    const reduced = Math.max(1, amount - this.armor);
    this.hp -= reduced;
    this.invuln = 600;
    this._flashTimer = 200;
    if (this.hp <= 0 && this.reviveTokens > 0) {
      this.reviveTokens -= 1;
      this.hp = Math.ceil(this.maxHp * 0.5);
      this.invuln = 1500;
      return 'revived';
    }
    return true;
  }

  heal(n) {
    this.hp = Math.min(this.maxHp, this.hp + n);
  }

  preUpdateTick(delta) {
    if (this.invuln > 0) this.invuln -= delta;
    if (this._flashTimer > 0) {
      this._flashTimer -= delta;
      this.setTintFill(0xffffff);
      if (this._flashTimer <= 0) this.clearTint();
    } else if (this.invuln > 0) {
      this.setAlpha(Math.sin(this.scene.time.now / 40) > 0 ? 0.5 : 1);
    } else {
      this.setAlpha(1);
    }
    if (this.regenPerSec > 0 && this.hp < this.maxHp) {
      this.regenAccum += (this.regenPerSec * delta) / 1000;
      while (this.regenAccum >= 1) {
        this.regenAccum -= 1;
        this.hp = Math.min(this.maxHp, this.hp + 1);
      }
    }
  }
}

// --------------------------------------------------------------
// ENEMY
// --------------------------------------------------------------
export class Enemy extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, def, hpMult = 1, speedMult = 1) {
    const tex = def.isBoss ? 'b_' + def.name : 'e_' + def.name;
    super(scene, x, y, tex);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.def = def;
    this.maxHp = def.hp * hpMult;
    this.hp = this.maxHp;
    this.speed = def.speed * speedMult;
    this.damage = def.damage;
    this.xpValue = def.xp;
    this.creditValue = def.credits;
    this.ai = def.ai;
    this.isBoss = !!def.isBoss;
    this.fireDelay = def.fireDelay || 0;
    this.fireTimer = (def.fireDelay || 0) * Math.random();
    this.spawnTime = scene.time.now;
    this.setDepth(this.isBoss ? 15 : 10);
    this.body.setSize(def.size * 0.85, def.size * 0.85);

    this._wigglePhase = Math.random() * Math.PI * 2;
    this._sweepPhase = Math.random() * Math.PI * 2;
    this._diveTarget = null;
    this._flashTimer = 0;
  }

  hit(dmg) {
    this.hp -= dmg;
    this._flashTimer = 80;
    this.setTintFill(0xffffff);
    return this.hp <= 0;
  }

  updateAI(time, delta, scene) {
    const dt = delta / 1000;
    const v = this.body.velocity;

    switch (this.ai) {
      case 'descend':
        v.set(0, this.speed);
        break;
      case 'descend_wiggle': {
        this._wigglePhase += dt * 4;
        v.set(Math.sin(this._wigglePhase) * 90, this.speed);
        break;
      }
      case 'shoot': {
        // hovers and weaves — no bullets, contact only
        const hoverY = 140 + Math.sin(this._sweepPhase + time / 800) * 20;
        if (this.y < hoverY) {
          v.set(0, this.speed);
        } else {
          v.set(Math.sin(time / 500 + this._sweepPhase) * 80, Math.cos(time / 700) * 10);
        }
        break;
      }
      case 'dive': {
        if (!this._diveTarget && scene.player.active) {
          this._diveTarget = { x: scene.player.x, y: scene.player.y };
        }
        if (this._diveTarget) {
          const dx = this._diveTarget.x - this.x;
          const sign = Math.sign(dx);
          v.set(sign * Math.min(120, Math.abs(dx) * 4), this.speed);
        } else {
          v.set(0, this.speed);
        }
        break;
      }
      case 'sweep': {
        this._sweepPhase += dt * 1.5;
        v.set(Math.sin(this._sweepPhase) * 140, this.speed * 0.7);
        break;
      }
      case 'boss': {
        // sweeps menacingly across the upper screen — no projectiles
        const t = time / 1000;
        v.set(Math.sin(t * 0.8) * 90, this.y < 130 ? this.speed : Math.sin(t * 0.6) * 30);
        break;
      }
      case 'boss_spawn': {
        const t = time / 1000;
        v.set(Math.sin(t * 0.6) * 60, this.y < 140 ? this.speed : Math.sin(t * 0.4) * 20);
        this.fireTimer -= delta;
        if (this.fireTimer <= 0) {
          scene.spawnEnemyAt('swarm', this.x + (Math.random() - 0.5) * 40, this.y + 30);
          this.fireTimer = this.fireDelay;
        }
        break;
      }
      default:
        v.set(0, this.speed);
    }

    if (this._flashTimer > 0) {
      this._flashTimer -= delta;
      if (this._flashTimer <= 0) this.clearTint();
    }

    if (this.y > scene.scale.height + 60) {
      this.destroy();
    }
  }
}

// --------------------------------------------------------------
// BULLET (player)
// --------------------------------------------------------------
export class Bullet extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    super(scene, x, y, 'bullet');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setDepth(8);
    this.body.setAllowGravity(false);
    this.damage = 1;
    this.pierce = 0;
    this._hitSet = new Set();
  }
  fire(vx, vy, damage, pierce) {
    this.enableBody(true, this.x, this.y, true, true);
    this.body.setAllowGravity(false);
    this.body.setVelocity(vx, vy);
    this.damage = damage;
    this.pierce = pierce || 0;
    this._hitSet.clear();
    this.rotation = Math.atan2(vy, vx) + Math.PI / 2;
  }
}

// --------------------------------------------------------------
// MISSILE — homes onto nearest enemy
// --------------------------------------------------------------
export class Missile extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    super(scene, x, y, 'missile');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setDepth(8);
    this.body.setAllowGravity(false);
    this.damage = 1;
    this.blast = 50;
    this.target = null;
    this.life = 4000;
    this.speed = 220;
  }
  fire(angle, damage, blast) {
    this.enableBody(true, this.x, this.y, true, true);
    this.body.setAllowGravity(false);
    this.damage = damage;
    this.blast = blast;
    this.life = 4000;
    this.target = null;
    this.body.setVelocity(Math.cos(angle) * this.speed, Math.sin(angle) * this.speed);
    this.rotation = angle + Math.PI / 2;
  }
  updateMissile(delta, enemies) {
    this.life -= delta;
    if (this.life <= 0) { this.disableBody(true, true); return; }

    if (!this.target || !this.target.active) {
      let best = null, bestD = 1e9;
      enemies.children.iterate((e) => {
        if (!e || !e.active) return;
        const d = Phaser.Math.Distance.Squared(this.x, this.y, e.x, e.y);
        if (d < bestD) { bestD = d; best = e; }
      });
      this.target = best;
    }
    if (this.target && this.target.active) {
      const desired = Math.atan2(this.target.y - this.y, this.target.x - this.x);
      const cur = Math.atan2(this.body.velocity.y, this.body.velocity.x);
      const diff = Phaser.Math.Angle.Wrap(desired - cur);
      const maxTurn = 5 * (delta / 1000);
      const turn = Phaser.Math.Clamp(diff, -maxTurn, maxTurn);
      const next = cur + turn;
      this.body.setVelocity(Math.cos(next) * this.speed, Math.sin(next) * this.speed);
      this.rotation = next + Math.PI / 2;
    }
  }
}

// --------------------------------------------------------------
// DRONE — orbits the player, contact-damages enemies
// --------------------------------------------------------------
export class Drone extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, player, indexOffset) {
    super(scene, player.x, player.y, 'drone');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.player = player;
    this.angleOffset = indexOffset;
    this.setDepth(9);
    this.body.setAllowGravity(false);
    this.body.setCircle(7);
    this._cooldowns = new Map();
    this.damage = 2;
    this.radius = 70;
    this.spinSpeed = 2.4;
  }
  updateDrone(time) {
    const a = this.angleOffset + (time / 1000) * this.spinSpeed;
    this.x = this.player.x + Math.cos(a) * this.radius;
    this.y = this.player.y + Math.sin(a) * this.radius;
    if (this._cooldowns.size > 32) {
      for (const [k, t] of this._cooldowns) {
        if (time - t > 1000) this._cooldowns.delete(k);
      }
    }
  }
  canHit(enemy, time) {
    const t = this._cooldowns.get(enemy);
    if (t && time - t < 500) return false;
    this._cooldowns.set(enemy, time);
    return true;
  }
}

// --------------------------------------------------------------
// PICKUPS (XP gems, credits, heal, magnet)
// --------------------------------------------------------------
export function makePickup(scene, x, y, kind, value) {
  let tex = 'gem_s';
  if (kind === 'gem') {
    tex = value >= 8 ? 'gem_l' : value >= 3 ? 'gem_m' : 'gem_s';
  } else if (kind === 'credit') tex = 'credit';
  else if (kind === 'heal') tex = 'heal';
  else if (kind === 'magnet') tex = 'magnet';
  const p = scene.physics.add.sprite(x, y, tex);
  // Critical: register the pickup in the scene's pickups group so the
  // physics overlap with the player AND the magnet loop in updatePickups
  // can actually see it. Without this, every pickup is an orphan sprite.
  scene.pickups.add(p);
  p.kind = kind;
  p.value = value;
  p.setDepth(7);
  p.body.setAllowGravity(false);
  if (kind === 'gem') {
    p.setScale(1.7);
    scene.tweens.add({
      targets: p,
      scale: { from: 1.45, to: 1.95 },
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  } else {
    p.setScale(1.35);
  }
  const ang = Math.random() * Math.PI * 2;
  const spd = 60 + Math.random() * 80;
  p.body.setVelocity(Math.cos(ang) * spd, Math.sin(ang) * spd - 40);
  p.body.setDrag(180, 180);
  p.magnetized = false;
  p._spawnTime = scene.time.now;
  return p;
}
